/**
 * Todo メインページ
 *
 * タスク一覧ビューを全幅で表示する。
 */

import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Bell,
  BellOff,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleCheck,
  Copy,
  File,
  FileText,
  FolderOpen,
  GripVertical,
  Kanban,
  List,
  Lock,
  Trash2,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { getTodo as apiGetTodo, updateTodo as apiUpdateTodo } from '@/api/services/todo'
import type { TodoResponse } from '@/api/types/todo'
import { FooterTabBar, type FooterTabSpec } from '@/components/shared/footer-tab-bar'
import { footerTabPanelProps } from '@/components/shared/footer-tab-bar.utils'
import { FullAccessFallback } from '@/components/shared/full-access-fallback'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { SearchBox } from '@/components/ui/search-box'
import { Skeleton } from '@/components/ui/skeleton'
import { usePushNotification } from '@/hooks/usePushNotification'
import { confirmDialog } from '@/lib/confirm-dialog'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'
import { selectCanUseTodo, useAppStore } from '@/store/useAppStore'
import { useTodoStore } from '@/store/useTodoStore'

import { CalendarPane } from './CalendarPane'
import { InlineEditCell, InlineTitleEdit } from './InlineEditCell'
import { KanbanPane } from './KanbanPane'
import { filterTodosByQuery } from './search'
import { compareTodoPosition } from './sort'
import { buildTodoImplementCommand, copyTodoImplementCommand } from './todo-command'
import { TodoEditSheet } from './TodoEditSheet'
import { TodoInput } from './TodoInput'
import { readStoredViewMode, shouldIncludeDone, storeViewMode, type TodoViewMode as ViewMode } from './view-mode'

const TODO_DRAWER_QUERY_KEY = 'todo'

const VIEW_TAB_ID_PREFIX = 'todo-view-tabs'

const energyChip = (energy: 'low' | 'medium' | 'high') => {
  if (energy === 'high') return 'bg-red-500/10 text-red-600 dark:text-red-300 border-red-500/30'
  if (energy === 'low') return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border-emerald-500/30'
  return 'bg-amber-500/10 text-amber-600 dark:text-amber-300 border-amber-500/30'
}

const formatDeadline = (iso: string | null | undefined) => {
  if (!iso) return null
  try {
    const d = parseUtcDate(iso)
    return d.toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export const TodoPage = () => {
  const navigate = useNavigate()
  const canUseTodo = useAppStore(selectCanUseTodo)
  const todos = useTodoStore((s) => s.todos)
  const isLoading = useTodoStore((s) => s.isLoading)
  const loadTodos = useTodoStore((s) => s.loadTodos)
  const patchTodo = useTodoStore((s) => s.patchTodo)
  const removeTodo = useTodoStore((s) => s.removeTodo)
  const duplicateTodo = useTodoStore((s) => s.duplicateTodo)

  const [searchParams, setSearchParams] = useSearchParams()
  const [showDone, setShowDone] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>(readStoredViewMode)
  const [editingTodo, setEditingTodo] = useState<TodoResponse | null>(null)
  const [collapsedTodos, setCollapsedTodos] = useState<Set<number>>(new Set())
  const [listGroupBy, setListGroupBy] = useState<'none' | 'project'>(
    () => (localStorage.getItem('todo-list-group') as 'none' | 'project') || 'none',
  )
  const [listSortBy, setListSortBy] = useState<'position' | 'priority' | 'deadline' | 'scheduled_start' | 'energy'>(
    () =>
      (localStorage.getItem('todo-list-sort') as 'position' | 'priority' | 'deadline' | 'scheduled_start' | 'energy') ||
      'position',
  )
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    () => (localStorage.getItem('todo-list-sort-order') as 'asc' | 'desc') || 'asc',
  )
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  const {
    isSubscribed,
    isLoading: pushLoading,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
    isSupported: pushSupported,
  } = usePushNotification()

  const handlePushToggle = async () => {
    if (isSubscribed) {
      const ok = await pushUnsubscribe()
      toast[ok ? 'success' : 'error'](ok ? '通知を無効にしました' : '通知の無効化に失敗しました')
    } else {
      const ok = await pushSubscribe()
      toast[ok ? 'success' : 'error'](ok ? '通知を有効にしました' : '通知の有効化に失敗しました')
    }
  }

  const openTodoDrawer = useCallback(
    (todo: TodoResponse) => {
      setEditingTodo(todo)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set(TODO_DRAWER_QUERY_KEY, String(todo.id))
        return next
      })
    },
    [setSearchParams],
  )

  const closeTodoDrawer = useCallback(() => {
    setEditingTodo(null)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(TODO_DRAWER_QUERY_KEY)
      return next
    })
  }, [setSearchParams])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.altKey && !e.shiftKey && !e.ctrlKey) {
        const map: Record<string, ViewMode> = {
          Digit1: 'list',
          Digit2: 'kanban',
          Digit3: 'calendar',
        }
        const mode = map[e.code]
        if (mode) {
          e.preventDefault()
          setViewMode(mode)
          storeViewMode(mode)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (!canUseTodo) return
    loadTodos({ include_done: shouldIncludeDone(viewMode, showDone) })
  }, [canUseTodo, showDone, viewMode, loadTodos])

  const fetchingDrawerTodoIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (!canUseTodo) return

    const drawerTodoIdRaw = searchParams.get(TODO_DRAWER_QUERY_KEY)
    if (!drawerTodoIdRaw) {
      fetchingDrawerTodoIdRef.current = null
      if (editingTodo) setEditingTodo(null)
      return
    }

    const drawerTodoId = Number(drawerTodoIdRaw)
    if (!Number.isInteger(drawerTodoId) || drawerTodoId <= 0) {
      fetchingDrawerTodoIdRef.current = null
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete(TODO_DRAWER_QUERY_KEY)
        return next
      })
      return
    }

    if (editingTodo?.id === drawerTodoId) return
    const todo = todos.find((item) => item.id === drawerTodoId)
    if (todo) {
      setEditingTodo(todo)
      return
    }

    // store にない（例: include_done=false 時の done todo を deep link）場合は API で単体取得
    if (fetchingDrawerTodoIdRef.current === drawerTodoId) return
    fetchingDrawerTodoIdRef.current = drawerTodoId
    let cancelled = false
    void apiGetTodo(drawerTodoId)
      .then((fetched) => {
        if (cancelled) return
        setEditingTodo(fetched)
      })
      .catch(() => {
        if (cancelled) return
        // 取得失敗（404 等）→ クエリをクリアして URL を整合させる
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev)
          next.delete(TODO_DRAWER_QUERY_KEY)
          return next
        })
      })
      .finally(() => {
        if (fetchingDrawerTodoIdRef.current === drawerTodoId) {
          fetchingDrawerTodoIdRef.current = null
        }
      })

    return () => {
      cancelled = true
    }
  }, [editingTodo, canUseTodo, searchParams, setSearchParams, todos])

  // parent_id → children マップ
  const childrenMap = useMemo(() => {
    const map = new Map<number, TodoResponse[]>()
    for (const t of todos) {
      if (t.parent_id != null) {
        const arr = map.get(t.parent_id) ?? []
        arr.push(t)
        map.set(t.parent_id, arr)
      }
    }
    // position でソート
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    }
    return map
  }, [todos])

  const pendingCount = useMemo(
    () => todos.filter((t) => !t.is_event && t.status !== 'done' && t.status !== 'dismissed').length,
    [todos],
  )

  const sortedTodos = useMemo(() => {
    const filtered = [...todos].filter((t) => !t.is_event && t.parent_id == null)
    if (listSortBy === 'position') return filtered.sort(compareTodoPosition)
    const dir = sortOrder === 'asc' ? 1 : -1
    return filtered.sort((a, b) => {
      switch (listSortBy) {
        case 'priority':
          return (b.priority - a.priority) * dir
        case 'deadline': {
          if (!a.deadline && !b.deadline) return 0
          if (!a.deadline) return 1
          if (!b.deadline) return -1
          return (new Date(a.deadline).getTime() - new Date(b.deadline).getTime()) * dir
        }
        case 'scheduled_start': {
          if (!a.scheduled_start && !b.scheduled_start) return 0
          if (!a.scheduled_start) return 1
          if (!b.scheduled_start) return -1
          return (new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime()) * dir
        }
        case 'energy': {
          const order = { high: 0, medium: 1, low: 2 }
          return (order[a.energy] - order[b.energy]) * dir
        }
      }
    })
  }, [todos, listSortBy, sortOrder])

  // 並び替えの position は全件で振り直すため、DnD は sortedTodos、表示はこちらを使う
  const visibleTodos = useMemo(
    () => filterTodosByQuery(sortedTodos, searchQuery, childrenMap),
    [sortedTodos, searchQuery, childrenMap],
  )

  // プロジェクトグルーピング
  const groupedTodos = useMemo(() => {
    if (listGroupBy !== 'project') return null
    const groups = new Map<string, TodoResponse[]>()
    for (const t of visibleTodos) {
      const key = t.project || ''
      const arr = groups.get(key) ?? []
      arr.push(t)
      groups.set(key, arr)
    }
    // プロジェクト名でソート（空=プロジェクトなしは最後）
    return [...groups.entries()].sort((a, b) => {
      if (!a[0]) return 1
      if (!b[0]) return -1
      return a[0].localeCompare(b[0], 'ja')
    })
  }, [visibleTodos, listGroupBy])

  // DnD
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = sortedTodos.findIndex((t) => t.id === active.id)
      const newIndex = sortedTodos.findIndex((t) => t.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = [...sortedTodos]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)

      // position を 1 始まりで振り直す（0 は新規タスク用に予約）
      const positionMap = new Map<number, number>()
      for (let i = 0; i < reordered.length; i++) {
        positionMap.set(reordered[i].id, i + 1)
      }

      // 楽観的に store を一括更新（アニメーション再発防止）
      useTodoStore.setState((state) => ({
        todos: state.todos.map((t) => {
          const newPos = positionMap.get(t.id)
          return newPos != null ? { ...t, position: newPos } : t
        }),
      }))

      // バックグラウンドで API 同期（store は更新しない）
      for (const [id, newPos] of positionMap) {
        const original = sortedTodos.find((t) => t.id === id)
        if ((original?.position ?? 0) !== newPos) {
          void apiUpdateTodo(id, { position: newPos })
        }
      }
    },
    [sortedTodos],
  )

  const IdCopyButton = ({ id, size = 'sm' }: { id: number; size?: 'sm' | 'xs' }) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        void copyTodoImplementCommand(id)
      }}
      aria-label={`「${buildTodoImplementCommand(id)}」をコピー`}
      className={`font-mono ${size === 'xs' ? 'text-[9px]' : 'text-[10px]'} text-muted-foreground underline underline-offset-2 hover:text-foreground p-0!`}
    >
      #{id}
    </button>
  )

  const renderTodoRow = (
    todo: TodoResponse,
    hasChildren: boolean,
    isCollapsed: boolean,
    doneCount: number,
    children: TodoResponse[],
    dragHandleProps: Record<string, unknown>,
  ) => (
    <>
      <tr
        className={`group/row transition-colors hover:bg-accent/40 cursor-pointer ${todo.status === 'done' ? 'opacity-50' : ''}`}
        onClick={() => openTodoDrawer(todo)}
      >
        <td className="px-1 py-1.5">
          <div className="flex items-center justify-center h-full">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground transition-colors cursor-grab touch-none p-0!"
              onClick={(e) => e.stopPropagation()}
              {...dragHandleProps}
            >
              <GripVertical className="size-3.5" />
            </button>
          </div>
        </td>
        <td className="px-1 py-1.5">
          <div className="flex items-center justify-center h-full">
            <button
              type="button"
              aria-label={todo.status === 'done' ? '未完了に戻す' : '完了にする'}
              className="text-muted-foreground hover:text-foreground transition-colors p-0!"
              onClick={(e) => {
                e.stopPropagation()
                void patchTodo(todo.id, {
                  status: todo.status === 'done' ? 'pending' : 'done',
                })
              }}
            >
              {todo.status === 'done' ? (
                <CircleCheck className="size-4 text-emerald-500" />
              ) : (
                <Circle className="size-4" />
              )}
            </button>
          </div>
        </td>
        <td className="px-1 py-1.5 whitespace-nowrap">
          <IdCopyButton id={todo.id} />
        </td>
        <td className="px-2 py-1.5 max-w-0 w-full overflow-hidden">
          <div className="flex items-center gap-1 min-w-0">
            <button
              type="button"
              className="shrink-0 text-muted-foreground hover:text-blue-500 transition-colors p-0!"
              onClick={(e) => {
                e.stopPropagation()
                openTodoDrawer(todo)
              }}
              aria-label={todo.has_memo ? 'メモを開く' : '詳細を開く'}
            >
              {todo.has_memo ? <FileText className="size-3.5" /> : <File className="size-3.5" />}
            </button>
            {hasChildren && (
              <button
                type="button"
                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0! pe-1!"
                onClick={(e) => {
                  e.stopPropagation()
                  setCollapsedTodos((prev) => {
                    const next = new Set(prev)
                    if (next.has(todo.id)) next.delete(todo.id)
                    else next.add(todo.id)
                    return next
                  })
                }}
                aria-label={isCollapsed ? 'サブタスクを展開' : 'サブタスクを折りたたむ'}
              >
                {isCollapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
              </button>
            )}
            <InlineTitleEdit
              todoId={todo.id}
              title={todo.title}
              className={`wrap-break-word line-clamp-2 ${todo.status === 'done' ? 'line-through text-muted-foreground' : ''}`}
            />
            {hasChildren && (
              <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">
                {doneCount}/{children.length}
              </span>
            )}
            {todo.blocked_by_id ? (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-orange-500 bg-orange-500/10 border border-orange-500/20 rounded px-1 py-px shrink-0 mt-0.5 max-w-32 truncate">
                <Lock className="size-2.5 shrink-0" />
                {todo.blocked_by_title ?? `#${todo.blocked_by_id}`}
              </span>
            ) : null}
          </div>
        </td>
        <InlineEditCell todoId={todo.id} field="energy" value={todo.energy} className="px-1 py-1.5">
          <span className={`text-[10px] px-1 py-px rounded border ${energyChip(todo.energy)}`}>
            {todo.energy.charAt(0).toUpperCase()}
          </span>
        </InlineEditCell>
        <InlineEditCell
          todoId={todo.id}
          field="deadline"
          value={todo.deadline ?? ''}
          className="px-1 py-1.5 text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis"
        >
          {formatDeadline(todo.deadline) ?? '—'}
        </InlineEditCell>
        <InlineEditCell
          todoId={todo.id}
          field="scheduled_start"
          value={todo.scheduled_start ?? ''}
          className="px-1 py-1.5 text-xs text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis"
        >
          {formatDeadline(todo.scheduled_start) ?? '—'}
        </InlineEditCell>
        <InlineEditCell
          todoId={todo.id}
          field="estimated_minutes"
          value={todo.estimated_minutes?.toString() ?? ''}
          className="px-1 py-1.5 text-xs text-muted-foreground text-right tabular-nums"
        >
          {todo.estimated_minutes ? `${todo.estimated_minutes}m` : '—'}
        </InlineEditCell>
        <InlineEditCell
          todoId={todo.id}
          field="priority"
          value={todo.priority.toString()}
          className="px-0.5 py-1.5 text-xs text-muted-foreground text-right font-mono"
        >
          {todo.priority}
        </InlineEditCell>
        <td className="px-0.5 py-1.5">
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="size-6 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation()
                void duplicateTodo(todo)
                  .then(() => toast.success('タスクを複製しました'))
                  .catch(() => toast.error('複製に失敗しました'))
              }}
            >
              <Copy className="size-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 text-muted-foreground hover:text-destructive"
              onClick={async (e) => {
                e.stopPropagation()
                if (await confirmDialog({ title: '削除しますか？', destructive: true }))
                  void removeTodo(todo.id).catch(() => toast.error('削除に失敗しました'))
              }}
            >
              <Trash2 className="size-3" />
            </Button>
          </div>
        </td>
      </tr>
      {hasChildren &&
        !isCollapsed &&
        children.map((child) => (
          <tr
            key={`child-${child.id}`}
            className="bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer"
            onClick={() => openTodoDrawer(child)}
          >
            <td />
            <td />
            <td className="px-1 py-1 whitespace-nowrap">
              <IdCopyButton id={child.id} size="xs" />
            </td>
            <td colSpan={6} className="px-2 py-1">
              <div className="flex items-center gap-2 pl-5">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground transition-colors shrink-0 p-0!"
                  onClick={(e) => {
                    e.stopPropagation()
                    void patchTodo(child.id, {
                      status: child.status === 'done' ? 'pending' : 'done',
                    }).catch(() => toast.error('サブタスク更新に失敗しました'))
                  }}
                  aria-label={child.status === 'done' ? '未完了に戻す' : '完了にする'}
                >
                  {child.status === 'done' ? (
                    <CircleCheck className="size-3.5 text-emerald-500" />
                  ) : (
                    <Circle className="size-3.5" />
                  )}
                </button>
                <span className={`text-xs ${child.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>
                  {child.title}
                </span>
                {child.blocked_by_id ? (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-orange-500 bg-orange-500/10 border border-orange-500/20 rounded px-1 py-px shrink-0 max-w-24 truncate">
                    <Lock className="size-2.5 shrink-0" />
                    {child.blocked_by_title ?? `#${child.blocked_by_id}`}
                  </span>
                ) : null}
                {child.estimated_minutes != null && (
                  <span className="text-[10px] text-muted-foreground ml-auto">{child.estimated_minutes}m</span>
                )}
              </div>
            </td>
            <td />
          </tr>
        ))}
    </>
  )

  if (!canUseTodo) {
    return <FullAccessFallback />
  }

  return (
    <div className="flex flex-col h-screen bg-muted/20">
      <Header
        title={`Todo (${pendingCount}件)`}
        onClose={() => navigate(PATHS.HOME)}
        dropdownSections={
          pushSupported && !pushLoading
            ? [
                {
                  type: 'action' as const,
                  label: '通知',
                  items: [
                    {
                      key: 'toggle-notification',
                      label: isSubscribed ? '通知を無効' : '通知を有効',
                      icon: isSubscribed ? <Bell className="size-4" /> : <BellOff className="size-4" />,
                      onClick: handlePushToggle,
                    },
                  ],
                },
              ]
            : undefined
        }
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* ビュー切り替えツールバー: スクロール外に固定 */}
        <div className="shrink-0 border-b bg-background px-4 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Checkbox id="show-done" checked={showDone} onCheckedChange={(v) => setShowDone(v === true)} />
              <label htmlFor="show-done" className="text-sm cursor-pointer text-muted-foreground">
                完了済みも表示
              </label>
            </div>
            {viewMode === 'list' && (
              <div className="flex items-center gap-2 order-3 sm:order-0">
                <button
                  type="button"
                  onClick={() => {
                    const next = listGroupBy === 'none' ? 'project' : 'none'
                    setListGroupBy(next)
                    localStorage.setItem('todo-list-group', next)
                  }}
                  className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors ${
                    listGroupBy === 'project'
                      ? 'border-primary/50 bg-primary/10 text-primary'
                      : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40'
                  }`}
                >
                  <FolderOpen className="size-3" />
                  プロジェクト
                </button>
                <div className="flex items-center gap-0.5">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors ${
                          listSortBy !== 'position'
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40'
                        }`}
                      >
                        {listSortBy === 'position' && '並び替え'}
                        {listSortBy === 'priority' && '優先度'}
                        {listSortBy === 'deadline' && '締切'}
                        {listSortBy === 'scheduled_start' && '開始予定'}
                        {listSortBy === 'energy' && 'エネルギー'}
                        <ChevronDown className="size-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-28">
                      {(
                        [
                          ['position', '手動'],
                          ['priority', '優先度'],
                          ['deadline', '締切'],
                          ['scheduled_start', '開始予定'],
                          ['energy', 'エネルギー'],
                        ] as const
                      ).map(([value, label]) => (
                        <DropdownMenuItem
                          key={value}
                          onClick={() => {
                            setListSortBy(value)
                            localStorage.setItem('todo-list-sort', value)
                          }}
                          className={listSortBy === value ? 'bg-accent' : ''}
                        >
                          {label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {listSortBy !== 'position' && (
                    <button
                      type="button"
                      onClick={() => {
                        const next = sortOrder === 'asc' ? 'desc' : 'asc'
                        setSortOrder(next)
                        localStorage.setItem('todo-list-sort-order', next)
                      }}
                      className="text-xs text-primary hover:opacity-70 transition-opacity leading-none"
                    >
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                  )}
                </div>
              </div>
            )}
            {viewMode !== 'calendar' && (
              <div className="order-last flex w-full sm:ml-auto sm:w-60">
                <SearchBox
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="タイトル・プロジェクト・タグ・#ID"
                />
              </div>
            )}
          </div>
        </div>

        {/* タスクビュー */}
        <div className="flex-1 overflow-y-auto" {...footerTabPanelProps(VIEW_TAB_ID_PREFIX, viewMode)}>
          <div className="p-4 flex flex-col gap-4">
            <TodoInput />
            {viewMode === 'calendar' ? (
              <div className="-mx-4 -mb-4">
                <CalendarPane
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  onTodoClick={openTodoDrawer}
                />
              </div>
            ) : viewMode === 'kanban' ? (
              <KanbanPane todos={visibleTodos} onCardClick={(t) => openTodoDrawer(t)} />
            ) : (
              <div className="flex flex-col">
                {isLoading && todos.length === 0 ? (
                  Array.from({ length: 4 }, (_, i) => `skeleton-${i}`).map((id) => (
                    <Skeleton key={id} className="h-8 w-full" />
                  ))
                ) : visibleTodos.length === 0 ? (
                  <Card className="p-8 text-center border-dashed">
                    <p className="text-sm text-muted-foreground">
                      {sortedTodos.length > 0
                        ? '検索に一致する Todo がありません。'
                        : 'Todo がありません。上の入力欄から追加してください。'}
                    </p>
                  </Card>
                ) : (
                  <div className="border rounded-lg overflow-x-auto">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50 text-xs text-muted-foreground">
                            <th className="w-6 px-0.5 py-1.5" />
                            <th className="w-7 px-1 py-1.5" />
                            <th className="text-left px-1 py-1.5 font-medium whitespace-nowrap">ID</th>
                            <th className="text-left px-2 py-1.5 font-medium min-w-62.5">タイトル</th>
                            <th className="text-left px-1 py-1.5 font-medium whitespace-nowrap">En</th>
                            <th className="text-left px-1 py-1.5 font-medium whitespace-nowrap">締切</th>
                            <th className="text-left px-1 py-1.5 font-medium whitespace-nowrap">開始</th>
                            <th className="text-right px-1 py-1.5 font-medium whitespace-nowrap">見積</th>
                            <th className="text-right px-0.5 py-1.5 font-medium">P</th>
                            <th className="w-14 px-0.5 py-1.5" />
                          </tr>
                        </thead>
                        {groupedTodos ? (
                          <SortableContext items={visibleTodos.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                            {groupedTodos.map(([project, items]) => {
                              const groupKey = project || '_none'
                              const isGroupCollapsed = collapsedGroups.has(groupKey)
                              const doneCount = items.filter((t) => t.status === 'done').length
                              return (
                                <React.Fragment key={groupKey}>
                                  <tbody>
                                    <tr
                                      className="border-b border-rose-500/20 bg-rose-500/8 cursor-pointer hover:bg-rose-500/15 transition-colors"
                                      onClick={() =>
                                        setCollapsedGroups((prev) => {
                                          const next = new Set(prev)
                                          if (next.has(groupKey)) next.delete(groupKey)
                                          else next.add(groupKey)
                                          return next
                                        })
                                      }
                                    >
                                      <td colSpan={10} className="px-3 py-2 border-l-2 border-l-rose-500/50">
                                        <div className="flex items-center gap-2">
                                          {isGroupCollapsed ? (
                                            <ChevronRight className="size-3.5 text-rose-400" />
                                          ) : (
                                            <ChevronDown className="size-3.5 text-rose-400" />
                                          )}
                                          <FolderOpen className="size-3.5 text-rose-400" />
                                          <span className="text-xs font-semibold text-rose-300">
                                            {project || 'プロジェクトなし'}
                                          </span>
                                          <span className="text-[10px] text-rose-400/60">
                                            {doneCount}/{items.length}
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                  {!isGroupCollapsed &&
                                    items.map((todo) => {
                                      const children = childrenMap.get(todo.id) ?? []
                                      const hasChildren = children.length > 0
                                      const isCollapsed = collapsedTodos.has(todo.id)
                                      const childDoneCount = children.filter((c) => c.status === 'done').length
                                      return (
                                        <SortableTodoGroup key={todo.id} id={todo.id}>
                                          {(dragHandleProps) => (
                                            <>
                                              {renderTodoRow(
                                                todo,
                                                hasChildren,
                                                isCollapsed,
                                                childDoneCount,
                                                children,
                                                dragHandleProps,
                                              )}
                                            </>
                                          )}
                                        </SortableTodoGroup>
                                      )
                                    })}
                                </React.Fragment>
                              )
                            })}
                          </SortableContext>
                        ) : (
                          <SortableContext items={visibleTodos.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                            {visibleTodos.map((todo) => {
                              const children = childrenMap.get(todo.id) ?? []
                              const hasChildren = children.length > 0
                              const isCollapsed = collapsedTodos.has(todo.id)
                              const doneCount = children.filter((c) => c.status === 'done').length
                              return (
                                <SortableTodoGroup key={todo.id} id={todo.id}>
                                  {(dragHandleProps) => (
                                    <>
                                      {renderTodoRow(
                                        todo,
                                        hasChildren,
                                        isCollapsed,
                                        doneCount,
                                        children,
                                        dragHandleProps,
                                      )}
                                    </>
                                  )}
                                </SortableTodoGroup>
                              )
                            })}
                          </SortableContext>
                        )}
                      </table>
                    </DndContext>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* フッタービュー切替タブバー */}
        <FooterTabBar<ViewMode>
          idPrefix={VIEW_TAB_ID_PREFIX}
          ariaLabel="タスク表示形式の切り替え"
          activeValue={viewMode}
          onChange={(mode) => {
            setViewMode(mode)
            storeViewMode(mode)
          }}
          tabs={
            [
              { value: 'list', label: 'リスト', icon: List },
              { value: 'kanban', label: 'カンバン', icon: Kanban },
              { value: 'calendar', label: 'カレンダー', icon: CalendarDays },
            ] satisfies readonly FooterTabSpec<ViewMode>[]
          }
        />
      </div>

      <TodoEditSheet
        todo={editingTodo}
        open={editingTodo !== null}
        onOpenChange={(open) => {
          if (!open) closeTodoDrawer()
        }}
      />
    </div>
  )
}

/**
 * 親タスク＋サブタスクのグループをドラッグ可能にするラッパー。
 * HTML仕様上 table 内に複数 tbody を配置可能なため、tbody を sortable 単位として使用する。
 */
const SortableTodoGroup = ({
  id,
  children,
}: {
  id: number
  children: (dragHandleProps: Record<string, unknown>) => React.ReactNode
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <tbody ref={setNodeRef} style={style} className="border-b last:border-b-0">
      {children({ ...attributes, ...listeners })}
    </tbody>
  )
}
